

import { Button } from '@repo/ui/components/ui/button';
import { useRouter } from '@repo/ui/hooks/use-router';
import { InventoryService } from '@repo/ui/lib/api/services/inventory.service';
import { ArrowLeft, Download } from 'lucide-react';
import React, { useCallback, useState } from 'react';

import { PrintPage } from './components/PrintPage';
import { PrintStyles } from './components/PrintStyles';
import { useInventoryData } from '@repo/ui/hooks/use-inventory-data';
import { PrintDataFields } from './hooks/use-print-data';
import { useMultiPrintData } from '../../../../ui/src/features/inventaires/hooks/use-multi-print-data';
import { MultiPDFDocument } from '../pdf';
import { pdf } from '@react-pdf/renderer';
import { usePDFDownload } from '../../../../ui/src/features/inventaires/print/hooks';
import { usePDFDownloadMulti } from '../../../../ui/src/features/inventaires/print/hooks/use-pdf-download';


interface MultiPrintArbreProps {

}

export default function MultiPrintArbre({ }: MultiPrintArbreProps = {}) {
  const router = useRouter();
  const [printMode, setPrintMode] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Generate PDF function
  const generatePDF = async (printDataList: PrintDataFields[], ids: number[]) => {
    try {
      const blob = await pdf(
        <MultiPDFDocument printDataList={printDataList} arbreIds={ids} />
      ).toBlob();
      console.log('blob', blob);
      if (!blob) {
        throw new Error('Impossible de générer le blob PDF');
      }
      return blob;
    } catch (error) {
      console.error('Erreur génération PDF:', error);

    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Load inventaire IDs from storage
  const [inventaireIds, setInventaireIds] = React.useState<number[]>([]);
  const [inventaires, setInventaires] = React.useState<any[]>([]);
  const [isInitializing, setIsInitializing] = React.useState(true);


  React.useEffect(() => {
    const inventoryService = new InventoryService();
    const storedInventaires = inventoryService.getInventairesToPrint();

    if (storedInventaires && storedInventaires.length > 0) {
      // Extract IDs from stored inventaires
      const ids = storedInventaires.map((inv: any) => inv?.id).filter((id: any): id is number => !!id);
      setInventaireIds(ids);
      setInventaires(storedInventaires);
    }
    setIsInitializing(false);
  }, []);

  // Fetch print data from backend using the new hook
  const {
    printDataList: printDataFromBackend,
    isLoading: isLoadingPrintData,
    hasError: hasBackendError,
  } = useMultiPrintData(inventaireIds.length > 0 ? inventaireIds : []);



  // Use backend data if available (even if some requests failed), otherwise fallback to legacy
  const printDataList = printDataFromBackend as PrintDataFields[];
  const isLoading = isInitializing || (isLoadingPrintData && printDataList.length === 0);


  const handleDownloadPDF = usePDFDownloadMulti(generatePDF, inventaireIds, {
    filename: `arbres-${printDataList.length}-fiches.pdf`,
  });

  const _handleDownloadPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      await handleDownloadPDF(printDataList);
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      alert(`Erreur lors de la génération du PDF: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setIsGeneratingPDF(false);
    }
  };



  const handleBack = () => {
    // Clean up stored data when leaving
    const inventoryService = new InventoryService();
    inventoryService.destroyInventairesToPrint();
    router.back();
  };



  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-lg">Chargement des inventaires...</p>
        </div>
      </div>
    );
  }

  if (!isLoading && (!inventaireIds || inventaireIds.length === 0)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Erreur</h1>
          <p className="text-muted-foreground mb-4">
            Aucun inventaire sélectionné pour l'impression
          </p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white ${printMode ? 'print-mode' : 'min-h-screen'}`}>
      {/* Print Controls - Hidden when printing */}
      {!printMode && (
        <div className="print:hidden bg-white shadow-sm border-b mb-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <button
                onClick={handleBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
                type="button"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Retour</span>
              </button>

              <div className="flex items-center space-x-4">
                {/* <Button
                  onClick={handlePrint}
                  className="bg-primary hover:bg-primary"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  IMPRIMER ({inventaires.length} inventaires)
                </Button> */}
                <Button
                  onClick={_handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary hover:text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isGeneratingPDF ? 'Génération...' : 'TÉLÉCHARGER PDF'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Print Content */}
      <div className="print-container">
        {printDataList.map((printData, index) => {
          // Use inventaire ID from backend data or fallback to stored inventaire
          const arbreId = inventaireIds[index] || inventaires[index]?.arbre?.id || inventaires[index]?.id || index;

          return (
            <div
              key={arbreId}
              className={index > 0 ? 'page-break-before' : ''}
            >
              <PrintPage
                data={printData}
                inventaireId={arbreId}
              />
            </div>
          );
        })}
      </div>




    </div>
  );
}